import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, dateIT } from "@/lib/format";
import { toast } from "sonner";
import { PayPalCheckout, SavedMethod, pmLabel } from "@/components/PayPalCheckout";
import { Wallet, Plus, Mail, MessageCircle, Send, X, ArrowDownLeft, ArrowUpRight, Sparkles, RefreshCw } from "lucide-react";

export default function Messages() {
  const { org, loadMe } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [props, setProps] = useState([]);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showSend, setShowSend] = useState(false);

  const load = async () => {
    const [w, t, m, p] = await Promise.all([api.get("/wallet"), api.get("/wallet/transactions"), api.get("/messages"), api.get("/properties")]);
    setWallet(w.data); setTxs(t.data); setMsgs(m.data); setProps(p.data);
  };
  useEffect(() => { load(); }, []);

  const done = async () => { toast.success("Borsellino ricaricato"); setShowRecharge(false); await load(); await loadMe(); };

  const icon = (t) => t === "email" ? Mail : t === "whatsapp" ? MessageCircle : t === "ai" ? Sparkles : t === "recharge" ? ArrowDownLeft : ArrowUpRight;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Borsellino & Messaggi</h1><p className="text-muted-foreground text-sm mt-1">Credito prepagato per comunicare con gli ospiti</p></div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-gradient-to-br from-[hsl(168,62%,14%)] to-[hsl(190,55%,22%)] text-white rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <Wallet className="h-28 w-28 absolute -right-5 -bottom-5 opacity-10" />
          <div className="text-xs uppercase tracking-wider opacity-80">Credito Disponibile</div>
          <div className="font-mono-data text-4xl font-bold mt-2" data-testid="wallet-balance-display">{eur(wallet?.balance_cents)}</div>
          <div className="flex gap-2 mt-4">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-white/15">Email {eur(wallet?.rates?.email_cents)}</span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-white/15">WhatsApp {eur(wallet?.rates?.whatsapp_cents)}</span>
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={() => setShowRecharge(true)} className="flex-1 bg-white text-[hsl(168,62%,18%)] rounded-lg py-2.5 text-sm font-semibold hover:opacity-90 flex items-center justify-center gap-1" data-testid="wallet-recharge-button"><Plus className="h-4 w-4" /> Ricarica</button>
            <button onClick={() => setShowSend(true)} className="flex-1 bg-white/15 hover:bg-white/25 rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-1"><Send className="h-4 w-4" /> Invia</button>
          </div>
        </div>

        <WalletMethod />

        <div className="lg:col-span-2 bg-card rounded-xl border border-border/80 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold">Movimenti</h2>
            <button onClick={load} className="text-muted-foreground hover:text-foreground"><RefreshCw className="h-4 w-4" /></button>
          </div>
          <div className="divide-y divide-border/60 max-h-[340px] overflow-y-auto">
            {txs.map((t) => { const Icon = icon(t.type); const neg = t.amount_cents < 0; return (
              <div key={t.id} className="py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-3"><div className={`h-8 w-8 rounded-lg grid place-items-center ${neg ? "bg-rose-500/10 text-rose-600" : "bg-emerald-500/10 text-emerald-600"}`}><Icon className="h-4 w-4" /></div>
                  <div><div className="text-sm font-medium">{t.description}</div><div className="text-[11px] text-muted-foreground">{dateIT(t.created_at)}</div></div></div>
                <span className={`font-mono-data font-semibold text-sm ${neg ? "text-rose-600" : "text-emerald-600"}`}>{neg ? "" : "+"}{eur(t.amount_cents)}</span>
              </div>
            ); })}
            {txs.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun movimento.</p>}
          </div>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border/80 p-5">
        <h2 className="font-display text-lg font-semibold mb-3">Messaggi inviati</h2>
        <div className="divide-y divide-border/60 max-h-[300px] overflow-y-auto">
          {msgs.map((m) => (
            <div key={m.id} className="py-3 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">{m.channel === "email" ? <Mail className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}</div>
                <div className="min-w-0"><div className="text-sm font-medium">{m.to}</div><div className="text-xs text-muted-foreground truncate max-w-md">{m.text}</div></div>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">−{eur(m.cost_cents)}</span>
            </div>
          ))}
          {msgs.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun messaggio inviato.</p>}
        </div>
      </div>

      {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} onDone={done} />}
      {showSend && <SendModal props={props} onClose={() => setShowSend(false)} onSent={async () => { await load(); await loadMe(); }} />}
    </div>
  );
}

function WalletMethod() {
  const updateCard = new URLSearchParams(window.location.search).get("update_card") === "1";
  const [m, setM] = useState(undefined);
  useEffect(() => { api.get("/paypal/method").then(({ data }) => setM(data.method)); }, []);
  if (m === undefined || (!m && !updateCard)) return null;
  return (
    <div className="lg:col-span-3 bg-card rounded-xl border border-border/80 p-5 space-y-3" data-testid="wallet-payment-method">
      <h2 className="font-display text-lg font-semibold">Metodo di pagamento salvato</h2>
      <SavedMethod method={m} startEdit={updateCard} onChange={setM} />
    </div>
  );
}

function RechargeModal({ onClose, onDone }) {
  const [amount, setAmount] = useState(2000);
  const [custom, setCustom] = useState("");
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/paypal/method").then(({ data }) => setSaved(data.method)); }, []);
  const cents = custom ? Math.round(Number(custom) * 100) : amount;
  const valid = cents >= 500 && cents <= 100000;
  const createOrder = (source) => api.post("/wallet/paypal/order", { amount_cents: cents, source }).then((r) => r.data.id);
  const onPaid = async (orderID) => { await api.post("/wallet/paypal/capture", { order_id: orderID }); await onDone(); };
  const paySaved = async () => {
    setBusy(true);
    try { await api.post("/wallet/recharge", { amount_cents: cents }); await onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Pagamento non riuscito"); }
    setBusy(false);
  };
  return (
    <Modal onClose={onClose} title="Ricarica borsellino">
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[1000, 2000, 5000].map((c) => <button key={c} onClick={() => { setAmount(c); setCustom(""); }} className={`py-3 rounded-lg border-2 font-semibold ${!custom && amount === c ? "border-slate-900 bg-[#E3FF00]" : "border-border"}`} data-testid={`recharge-amount-${c}`}>{eur(c)}</button>)}
      </div>
      <input type="number" min="5" max="1000" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Altro importo € (5–1.000)" className="w-full bg-background border border-input rounded-lg px-3 py-2.5 text-sm mb-4" data-testid="recharge-custom" />
      {!valid ? <p className="text-sm text-rose-600">Importo tra 5 € e 1.000 €</p> : (
        <div className="space-y-3">
          {saved && <button disabled={busy} onClick={paySaved} className="w-full bg-[#E3FF00] text-[#09090b] font-semibold rounded-xl py-3" data-testid="recharge-pay-saved">Paga {eur(cents)} con {pmLabel(saved)}</button>}
          <PayPalCheckout key={cents} mode="order" label={`Paga ${eur(cents)}`} createOrder={createOrder} onPaid={onPaid} />
        </div>
      )}
    </Modal>
  );
}

function SendModal({ props, onClose, onSent }) {
  const [f, setF] = useState({ channel: "email", to: "", text: "", booking_ref: "" });
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!f.to || !f.text) return toast.error("Compila destinatario e testo");
    setSending(true);
    try { await api.post("/messages/send", f); toast.success("Messaggio inviato"); onClose(); await onSent(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Invio non riuscito"); }
    finally { setSending(false); }
  };
  return (
    <Modal onClose={onClose} title="Invia messaggio all'ospite">
      <div className="space-y-3">
        <div className="flex gap-2">
          <button onClick={() => setF({ ...f, channel: "email" })} className={`flex-1 py-2 rounded-lg text-sm font-medium border flex items-center justify-center gap-2 ${f.channel === "email" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid="wallet-send-email-button"><Mail className="h-4 w-4" /> Email</button>
          <button onClick={() => setF({ ...f, channel: "whatsapp" })} className={`flex-1 py-2 rounded-lg text-sm font-medium border flex items-center justify-center gap-2 ${f.channel === "whatsapp" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid="wallet-send-whatsapp-button"><MessageCircle className="h-4 w-4" /> WhatsApp</button>
        </div>
        <input value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder={f.channel === "email" ? "email@ospite.it" : "+39 333 1234567"} className="w-full bg-background border border-input rounded-lg px-3 py-2.5 text-sm" data-testid="send-to-input" />
        <textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} rows={4} placeholder="Scrivi il messaggio..." className="w-full bg-background border border-input rounded-lg px-3 py-2.5 text-sm" data-testid="send-text-input" />
        <button onClick={send} disabled={sending} className="w-full bg-primary text-primary-foreground py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="send-submit-button"><Send className="h-4 w-4" /> {sending ? "Invio..." : "Invia messaggio"}</button>
      </div>
    </Modal>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-display text-lg font-semibold">{title}</h3><button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button></div>
        {children}
      </div>
    </div>
  );
}
